import { Component, OnDestroy, OnInit, ViewChild, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { PaxOrderService } from '../../services/pax-order.service';
import { NavigationEnd, Router } from '@angular/router';
import { CheckUserRolesService } from '../../services/check-user-roles.service';
import { Observable, Subscription, Subject, filter, firstValueFrom} from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PopupMessageComponent } from '../popup-message/popup-message.component';
import { MultiSelectDropdownComponent } from '../multi-select-dropdown/multi-select-dropdown.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MultiSelectDropdownComponent,
    PopupMessageComponent
  ],
  selector: 'app-my-orders-view',
  templateUrl: './my-orders-view.component.html',
  styleUrls: ['./my-orders-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [PaxOrderService, DatePipe],
})
export class MyOrdersViewComponent implements OnInit, OnDestroy {
  accessToken$: Observable<unknown>;
  private destroy$ = new Subject<void>();

  currentPage = 1;
  totalPages: number;
  totalFoundItems: number;

  queryParameters: any = {};
  parcelNumber = signal('');
  partnerParcelNumber = signal('');
  tenantKey: string = null;
  pickUpDate: string = '';
  creationDate: string = '';
  insertTimeStampGt: any | Date = '';
  insertTimeStampLte: any | Date = '';
  pickupLocation = signal('');
  orderStatus = signal('');
  requesterName = signal('');
  requesterCountry: string = '';
  requesterReference = signal('');
  requesterLocation = signal('');
  deliveryLocation = signal('');
  selectedRequestedPickupDateGt = signal<Date | string>('');
  selectedRequestedPickupDateLte = signal<Date | string>('');
  requestedPickupDateGt = signal('');
  requestedPickupDateLte = signal('');
  selectedRequestedCreationDateGt = signal<Date | string>('');
  selectedRequestedCreationDateLte = signal<Date | string>('');
  requestedCreationDateGt = signal('');
  requestedCreationDateLte = signal('');
  private processingDateBlur: boolean = false;
  isGroupAdmin: boolean = false;
  limit: number = 30;
  offset: number = 0;
  ordersList = signal<any[]>([]);
  adjustedFilteredOrdersList = computed(() => {
    return this.ordersList().map(order => {
      const parcelDetailsExist = order.parcelDetails && order.parcelDetails.length > 0;
      return {
        paxOrderKey: order.paxOrderKey,
        parcelNumber: parcelDetailsExist ? order.parcelDetails[0]['parcelKey'] : 'Pending',
        partnerParcelNumber: order['partnerParcelNumber'],
        creationDate: this.dateOfInsertTimestamp(order.insertTimestamp),
        pickUpDate: order.requestedPickupDate,
        requesterName: order.requester['name'],
        requesterReference: order['requesterReference'],
        requesterLocation: order['requesterLocation'],
        pickupLocation: order['pickupLocation'],
        deliveryLocation: order['deliveryLocation'],
        orderStatus: order['operationalOrderStatus'],
        parcelDetails: parcelDetailsExist ? order.parcelDetails : [],
        statusKey: order.statusKey
      };
    });
  });
  paxOrderValdationsErrorList = [];
  dataNotLoaded: boolean = false;
  timerForTyping: any = false;
  isPaxOrderCreator: boolean = false;
  authorizationFailed: boolean = false;

  isResizing: boolean = false;
  startingX: number = 0;
  startingWidth: number = 0;
  resizingMouseMove: any;
  resizingMouseUp: any;

  currentPath: string = '';
  private subscription: Subscription;

  orderAsTemplate: any;

  expandedRows: { [key: number]: boolean } = {};
  orderStatusList: string[] = ['NEW', 'IN_PROGRESS', 'PICKED_UP', 'COMPLETE', 'CANCELLED'];
  selectedStatuses = signal<string[]>([]);

  // Computed filter chips - automatically updates when any filter changes
  filterChips = computed(() => {
    const chips: { key: string, value: string }[] = [];

    const formattedCreationStartDate = this.formatDateForDisplay(this.selectedRequestedCreationDateGt());
    const formattedCreationEndDate = this.formatDateForDisplay(this.selectedRequestedCreationDateLte());
    const formattedPickupStartDate = this.formatDateForDisplay(this.selectedRequestedPickupDateGt());
    const formattedPickupEndDate = this.formatDateForDisplay(this.selectedRequestedPickupDateLte());

    const parametersToCheck = [
      { key: 'Parcel Number', value: this.parcelNumber() },
      { key: 'Partner Parcel Number', value: this.partnerParcelNumber() },
      { key: 'Creation Date - Start', value: formattedCreationStartDate },
      { key: 'Creation Date - End', value: formattedCreationEndDate },
      { key: 'Pick-up Date - Start', value: formattedPickupStartDate },
      { key: 'Pick-up Date - End', value: formattedPickupEndDate },
      { key: 'Pick-up Location', value: this.pickupLocation() },
      { key: 'Requester Name', value: this.requesterName() },
      { key: 'Requester Reference', value: this.requesterReference() },
      { key: 'Requester Location', value: this.requesterLocation() },
      { key: 'Delivery Location', value: this.deliveryLocation() },
    ];

    // Add individual status chips
    this.selectedStatuses().forEach(status => {
      chips.push({ key: 'Order Status', value: status });
    });

    parametersToCheck.forEach(param => {
      if (param.value && param.value !== '') {
        chips.push({ key: param.key, value: param.value });
      }
    });

    return chips;
  });

  popoverIndex: number | null = null;

  sortingList = signal<string[]>(["-inserttimestamp"]);

  // Computed sorting flags - automatically derived from sortingList
  isParcelNumberDescendingSortingActive = computed(() =>
    this.sortingList().includes('-parcelnumber')
  );
  isParcelNumberAscendingSortingActive = computed(() =>
    this.sortingList().includes('parcelnumber')
  );
  isCreationDateDescendingSortingActive = computed(() =>
    this.sortingList().includes('-inserttimestamp')
  );
  isCreationDateAscendingSortingActive = computed(() =>
    this.sortingList().includes('inserttimestamp')
  );
  isRequesterLocationDescendingSortingActive = computed(() =>
    this.sortingList().includes('-requesterlocation')
  );
  isRequesterLocationAscendingSortingActive = computed(() =>
    this.sortingList().includes('requesterlocation')
  );
  isPickupLocationDescendingSortingActive = computed(() =>
    this.sortingList().includes('-pickuplocation')
  );
  isPickupLocationAscendingSortingActive = computed(() =>
    this.sortingList().includes('pickuplocation')
  );
  isDeliveryLocationDescendingSortingActive = computed(() =>
    this.sortingList().includes('-deliverylocation')
  );
  isDeliveryLocationAscendingSortingActive = computed(() =>
    this.sortingList().includes('deliverylocation')
  );

  // Computed active sortings - automatically updates when sortingList changes
  activeSortings = computed(() => {
    const sortings: { key: string, order: 'asc' | 'desc' }[] = [];

    this.sortingList().forEach(sortKey => {
      const isDescending = sortKey.startsWith('-');
      const cleanKey = isDescending ? sortKey.substring(1) : sortKey;
      const displayKey = this.getDisplayKeyForSort(cleanKey);
      sortings.push({
        key: displayKey,
        order: isDescending ? 'desc' : 'asc'
      });
    });

    return sortings;
  });

  isTableLoadingMore = false;
  allTableDataLoaded = false;

  private lastRequestId = 0;

  private lastScrollTop = 0;
  private popupCooldown = false;
  private lastScrollDirection: 'up' | 'down' | null = null;
  private isPopupShowing = false;

  constructor(private paxOrderService: PaxOrderService,
    private checkUserRolesService: CheckUserRolesService,
    private router: Router,
    private datePipe: DatePipe) {
  }

  ngOnInit() {
    this.loadCurrentOffsetFromLocalStorage();
    this.loadFiltersFromLocalStorage();
    this.loadSortingChipsFromLocalStorage();

    const cerationDateStartDateRemoved = localStorage.getItem('creationStartDateRemoved') === 'true';
    const cerationDateEndDateRemoved = localStorage.getItem('creationEndDateRemoved') === 'true';

    if (!this.selectedRequestedCreationDateGt() && !cerationDateStartDateRemoved) {
      this.selectedRequestedCreationDateGt.set(this.getStandardDate(-6));
      this.requestedCreationDateGt.set(this.getPreviousDate(this.selectedRequestedCreationDateGt()));
    }
    if (!this.selectedRequestedCreationDateLte() && !cerationDateEndDateRemoved) {
      this.selectedRequestedCreationDateLte.set(this.getStandardDate(0));
    }

    // Handle initial date setup for creation dates
    if (this.selectedRequestedCreationDateGt()) {
      const startDate = this.parseAndValidateDate(this.selectedRequestedCreationDateGt());
      if (startDate) {
        this.requestedCreationDateGt.set(this.getPreviousDate(startDate) + "T23:59:59Z");
      }
    }

    if (this.selectedRequestedCreationDateLte()) {
      const endDate = this.parseAndValidateDate(this.selectedRequestedCreationDateLte());
      if (endDate) {
        this.requestedCreationDateLte.set(this.datePipe.transform(endDate, 'yyyy-MM-dd') + "T23:59:59Z");
      }
    }

    this.tenantKey = this.checkUserRolesService.getTenantKey();
    this.isGroupAdmin = this.checkUserRolesService.isGroupAccount;
    this.isPaxOrderCreator = this.checkUserRolesService.getIsPaxOrderCreator();
    this.resetOffsetAndOrderList();
    this.getCurrentPath();
    this.updateTable();

    const scrollContainer = document.querySelector('.table-scroll-container');
    if (scrollContainer) {
      scrollContainer.addEventListener('wheel', this.onMouseWheelAtBottom, { passive: true });
    }
  }

  onMouseWheelAtBottom = (event: WheelEvent) => {
    const element = document.querySelector('.table-scroll-container') as HTMLElement;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 1;

    if (atBottom && event.deltaY > 0 && this.allTableDataLoaded) {
      // User is trying to scroll down while already at bottom
      this.triggerEndOfListPopup();
    }
  };

  getCurrentPath() {
    this.subscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      let navigationEvent = event as NavigationEnd;
      this.currentPath = navigationEvent.url;
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    const scrollContainer = document.querySelector('.table-scroll-container');
    if (scrollContainer) {
      scrollContainer.removeEventListener('wheel', this.onMouseWheelAtBottom);
    }
  }

  setQueryParameters() {
    if (this.parcelNumber() !== '') {
      this.queryParameters['parcel-key'] = this.parcelNumber().trim();
    }

    if (this.partnerParcelNumber() !== '') {
      this.queryParameters['partner-parcel-number'] = this.partnerParcelNumber().trim();
    }

    if (this.requestedCreationDateLte() !== '') {
      this.queryParameters['insert-timestamp-lte'] = this.requestedCreationDateLte().trim();
    }

    if (this.requestedCreationDateGt() !== '') {
      this.queryParameters['insert-timestamp-gt'] = this.requestedCreationDateGt().trim();
    }

    if (this.requestedPickupDateLte() !== '') {
      this.queryParameters['requested-pickup-date-lte'] = this.requestedPickupDateLte().trim();
    }

    if (this.requestedPickupDateGt() !== '') {
      this.queryParameters['requested-pickup-date-gt'] = this.requestedPickupDateGt().trim();
    }

    if (this.pickupLocation() !== '') {
      this.queryParameters['pickup-location'] = this.pickupLocation().trim();
    }

    if (this.requesterName() !== '') {
      this.queryParameters['requester-name'] = this.requesterName().trim();
    }

    if (this.requesterReference() !== '') {
      this.queryParameters['requester-reference'] = this.requesterReference().trim();
    }

    if (this.requesterLocation() !== '') {
      this.queryParameters['requester-location'] = this.requesterLocation().trim();
    }

    if (this.deliveryLocation() !== '') {
      this.queryParameters['delivery-location'] = this.deliveryLocation().trim();
    }

    if (this.orderStatus() !== '') {
      this.queryParameters['operational-parcel-status'] = this.orderStatus();
    }

    this.queryParameters['limit'] = this.limit;
    this.queryParameters['offset'] = this.offset;
    this.queryParameters['sort'] = this.sortingList();
    if (!this.isGroupAdmin) {
      this.queryParameters['requester-tenant-keys'] = this.tenantKey;

      //ISRS-17919
      if (this.tenantKey === 'PARTNER_ITELLALT') {
        this.queryParameters['exclude-requester-name'] = 'Ecoparcel';
      }
    }

    this.saveFiltersToLocalStorage();
  }

  removeFilterChip(chip: { key: string, value: string }) {
    this.resetOffsetAndOrderList();
    if (chip.key === 'Remove All Filters') {
      this.removeAllFilters();
      this.updateTable();
      return;
    }

    switch (chip.key) {
      case 'Parcel Number':
        this.parcelNumber.set('');
        break;
      case 'Partner Parcel Number':
        this.partnerParcelNumber.set('');
        break;
      case 'Creation Date - Start':
        this.selectedRequestedCreationDateGt.set('');
        this.requestedCreationDateGt.set('');
        localStorage.setItem('creationStartDateRemoved', 'true');
        break;
      case 'Creation Date - End':
        this.requestedCreationDateLte.set('');
        this.selectedRequestedCreationDateLte.set('');
        localStorage.setItem('creationEndDateRemoved', 'true');
        break;
      case 'Pick-up Date - Start':
        this.selectedRequestedPickupDateGt.set('');
        this.requestedPickupDateGt.set('');
        break;
      case 'Pick-up Date - End':
        this.selectedRequestedPickupDateLte.set('');
        this.requestedPickupDateLte.set('');
        break;
      case 'Pick-up Location':
        this.pickupLocation.set('');
        break;
      case 'Requester Name':
        this.requesterName.set('');
        break;
      case 'Requester Reference':
        this.requesterReference.set('');
        break;
      case 'Requester Location':
        this.requesterLocation.set('');
        break;
      case 'Delivery Location':
        this.deliveryLocation.set('');
        break;
      case 'Order Status':
        this.selectedStatuses.update(statuses => statuses.filter(status => status !== chip.value));
        this.orderStatus.set(this.selectedStatuses().join(','));
        break;
    }

    this.updateTable();
    this.saveFiltersToLocalStorage();
  }

  removeAllFilters() {
    this.resetOffsetAndOrderList();
    this.parcelNumber.set('');
    this.partnerParcelNumber.set('');
    this.selectedRequestedCreationDateGt.set('');
    this.selectedRequestedCreationDateLte.set('');
    this.requestedCreationDateGt.set('');
    this.requestedCreationDateLte.set('');
    this.selectedRequestedPickupDateGt.set('');
    this.selectedRequestedPickupDateLte.set('');
    this.requestedPickupDateGt.set('');
    this.requestedPickupDateLte.set('');
    this.pickupLocation.set('');
    this.requesterName.set('');
    this.requesterReference.set('');
    this.requesterLocation.set('');
    this.deliveryLocation.set('');
    this.selectedStatuses.set([]);
    this.orderStatus.set('');

    localStorage.removeItem('orderFilters');
    localStorage.removeItem('creationStartDateRemoved');
    localStorage.removeItem('creationEndDateRemoved');
    localStorage.setItem('creationStartDateRemoved', 'true');
    localStorage.setItem('creationEndDateRemoved', 'true');

    this.saveFiltersToLocalStorage();
  }

  onRemoveAllFiltersClick() {
    this.removeAllFilters();
    this.updateTable();
  }

  saveFiltersToLocalStorage() {
    const filters = {
      parcelNumber: this.parcelNumber(),
      partnerParcelNumber: this.partnerParcelNumber(),
      // Convert Date objects to ISO strings for storage
      selectedRequestedCreationDateGt: this.selectedRequestedCreationDateGt() instanceof Date
        ? (this.selectedRequestedCreationDateGt() as Date).toISOString()
        : this.selectedRequestedCreationDateGt(),
      selectedRequestedCreationDateLte: this.selectedRequestedCreationDateLte() instanceof Date
        ? (this.selectedRequestedCreationDateLte() as Date).toISOString()
        : this.selectedRequestedCreationDateLte(),
      requestedCreationDateGt: this.requestedCreationDateGt(),
      requestedCreationDateLte: this.requestedCreationDateLte(),
      // Convert Date objects to ISO strings for storage
      selectedRequestedPickupDateGt: this.selectedRequestedPickupDateGt() instanceof Date
        ? (this.selectedRequestedPickupDateGt() as Date).toISOString()
        : this.selectedRequestedPickupDateGt(),
      selectedRequestedPickupDateLte: this.selectedRequestedPickupDateLte() instanceof Date
        ? (this.selectedRequestedPickupDateLte() as Date).toISOString()
        : this.selectedRequestedPickupDateLte(),
      requestedPickupDateGt: this.requestedPickupDateGt(),
      requestedPickupDateLte: this.requestedPickupDateLte(),
      pickupLocation: this.pickupLocation(),
      requesterName: this.requesterName(),
      requesterReference: this.requesterReference(),
      requesterLocation: this.requesterLocation(),
      deliveryLocation: this.deliveryLocation(),
      selectedStatuses: this.selectedStatuses(),
      orderStatus: this.orderStatus(),
    };
    localStorage.setItem('orderFilters', JSON.stringify(filters));
  }

  loadFiltersFromLocalStorage() {
    const storedFilters = localStorage.getItem('orderFilters');
    const creationDateStartDateRemoved = localStorage.getItem('creationStartDateRemoved') === 'true';
    const creationDateEndDateRemoved = localStorage.getItem('creationEndDateRemoved') === 'true';

    if (storedFilters) {
      try {
        const filters = JSON.parse(storedFilters);
        this.parcelNumber.set(filters.parcelNumber || '');
        this.partnerParcelNumber.set(filters.partnerParcelNumber || '');

        // Handle creation date start
        if (!creationDateStartDateRemoved && (filters.selectedRequestedCreationDateGt || filters.requestedCreationDateGt)) {
          if (filters.selectedRequestedCreationDateGt) {
            const parsedStartDate = this.parseAndValidateDate(filters.selectedRequestedCreationDateGt);
            if (parsedStartDate) {
              this.selectedRequestedCreationDateGt.set(parsedStartDate);
              this.requestedCreationDateGt.set(this.getPreviousDate(parsedStartDate) + "T23:59:59Z");
            } else if (filters.requestedCreationDateGt) {
              // Fallback to backend format if parsing failed
              this.requestedCreationDateGt.set(filters.requestedCreationDateGt);
              const backendDate = this.parseAndValidateDate(filters.requestedCreationDateGt.replace("T23:59:59Z", ""));
              if (backendDate) {
                const displayDate = new Date(backendDate);
                displayDate.setDate(displayDate.getDate() + 1);
                this.selectedRequestedCreationDateGt.set(displayDate);
              }
            }
          } else if (filters.requestedCreationDateGt) {
            this.requestedCreationDateGt.set(filters.requestedCreationDateGt);
            const backendDate = this.parseAndValidateDate(filters.requestedCreationDateGt.replace("T23:59:59Z", ""));
            if (backendDate) {
              const displayDate = new Date(backendDate);
              displayDate.setDate(displayDate.getDate() + 1);
              this.selectedRequestedCreationDateGt.set(displayDate);
            }
          }
        } else {
          this.selectedRequestedCreationDateGt.set('');
          this.requestedCreationDateGt.set('');
        }

        // Handle creation date end
        if (!creationDateEndDateRemoved && (filters.selectedRequestedCreationDateLte || filters.requestedCreationDateLte)) {
          if (filters.selectedRequestedCreationDateLte) {
            const parsedEndDate = this.parseAndValidateDate(filters.selectedRequestedCreationDateLte);
            if (parsedEndDate) {
              this.selectedRequestedCreationDateLte.set(parsedEndDate);
              this.requestedCreationDateLte.set(this.datePipe.transform(parsedEndDate, 'yyyy-MM-dd') + "T23:59:59Z");
            } else if (filters.requestedCreationDateLte) {
              // Fallback to backend format if parsing failed
              this.requestedCreationDateLte.set(filters.requestedCreationDateLte);
              const backendDate = this.parseAndValidateDate(filters.requestedCreationDateLte.replace("T23:59:59Z", ""));
              if (backendDate) {
                this.selectedRequestedCreationDateLte.set(backendDate);
              }
            }
          } else if (filters.requestedCreationDateLte) {
            this.requestedCreationDateLte.set(filters.requestedCreationDateLte);
            const backendDate = this.parseAndValidateDate(filters.requestedCreationDateLte.replace("T23:59:59Z", ""));
            if (backendDate) {
              this.selectedRequestedCreationDateLte.set(backendDate);
            }
          }
        } else {
          this.selectedRequestedCreationDateLte.set('');
          this.requestedCreationDateLte.set('');
        }

        // Handle pickup date start
        if (filters.selectedRequestedPickupDateGt || filters.requestedPickupDateGt) {
          if (filters.selectedRequestedPickupDateGt) {
            const parsedPickupStartDate = this.parseAndValidateDate(filters.selectedRequestedPickupDateGt);
            if (parsedPickupStartDate) {
              this.selectedRequestedPickupDateGt.set(parsedPickupStartDate);
              this.requestedPickupDateGt.set(this.getPreviousDate(parsedPickupStartDate));
            } else if (filters.requestedPickupDateGt) {
              // Fallback to backend format if parsing failed
              this.requestedPickupDateGt.set(filters.requestedPickupDateGt);
              const backendDate = this.parseAndValidateDate(filters.requestedPickupDateGt);
              if (backendDate) {
                const displayDate = new Date(backendDate);
                displayDate.setDate(displayDate.getDate() + 1);
                this.selectedRequestedPickupDateGt.set(displayDate);
              }
            }
          } else if (filters.requestedPickupDateGt) {
            this.requestedPickupDateGt.set(filters.requestedPickupDateGt);
            const backendDate = this.parseAndValidateDate(filters.requestedPickupDateGt);
            if (backendDate) {
              const displayDate = new Date(backendDate);
              displayDate.setDate(displayDate.getDate() + 1);
              this.selectedRequestedPickupDateGt.set(displayDate);
            }
          }
        } else {
          this.selectedRequestedPickupDateGt.set('');
          this.requestedPickupDateGt.set('');
        }

        // Handle pickup date end
        if (filters.selectedRequestedPickupDateLte || filters.requestedPickupDateLte) {
          if (filters.selectedRequestedPickupDateLte) {
            const parsedPickupEndDate = this.parseAndValidateDate(filters.selectedRequestedPickupDateLte);
            if (parsedPickupEndDate) {
              this.selectedRequestedPickupDateLte.set(parsedPickupEndDate);
              this.requestedPickupDateLte.set(this.datePipe.transform(parsedPickupEndDate, 'yyyy-MM-dd') || '');
            } else if (filters.requestedPickupDateLte) {
              // Fallback to backend format if parsing failed
              this.requestedPickupDateLte.set(filters.requestedPickupDateLte);
              const backendDate = this.parseAndValidateDate(filters.requestedPickupDateLte);
              if (backendDate) {
                this.selectedRequestedPickupDateLte.set(backendDate);
              }
            }
          } else if (filters.requestedPickupDateLte) {
            this.requestedPickupDateLte.set(filters.requestedPickupDateLte);
            const backendDate = this.parseAndValidateDate(filters.requestedPickupDateLte);
            if (backendDate) {
              this.selectedRequestedPickupDateLte.set(backendDate);
            }
          }
        } else {
          this.selectedRequestedPickupDateLte.set('');
          this.requestedPickupDateLte.set('');
        }

        this.pickupLocation.set(filters.pickupLocation || '');
        this.requesterName.set(filters.requesterName || '');
        this.requesterReference.set(filters.requesterReference || '');
        this.requesterLocation.set(filters.requesterLocation || '');
        this.deliveryLocation.set(filters.deliveryLocation || '');
        this.selectedStatuses.set(filters.selectedStatuses || []);
        this.orderStatus.set(filters.orderStatus || '');
      } catch (error) {
        console.error('Error parsing stored filters:', error);
        // Reset to default values on parsing error
        this.selectedRequestedCreationDateGt.set('');
        this.selectedRequestedCreationDateLte.set('');
        this.requestedCreationDateGt.set('');
        this.requestedCreationDateLte.set('');
        this.selectedRequestedPickupDateGt.set('');
        this.selectedRequestedPickupDateLte.set('');
        this.requestedPickupDateGt.set('');
        this.requestedPickupDateLte.set('');
      }
    }
  }

  onOrderWithMultipleParcelNumbersClick(index: number) {
    this.expandedRows[index] = !this.expandedRows[index];
  }

  getParcelStatus(item: any, parcel: string): string {
    if (!item['parcelDetails']) {
      return '';
    }
    const parcelDetail = item['parcelDetails'].find((detail: any) => detail.parcelKey === parcel);
    return parcelDetail ? parcelDetail.operationalParcelStatus : '';
  }

  updateTable(): void {
    this.queryParameters = {};
    this.queryParameters['offset'] = this.offset;
    this.queryParameters['limit'] = this.limit;
    this.setQueryParameters();

    const currentRequestId = ++this.lastRequestId;
    this.isTableLoadingMore = true;

    this.paxOrderService.getOrders(this.queryParameters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: (response) => {
        if (currentRequestId !== this.lastRequestId) return; // Skip processing if a newer request has already been initiated

        const newData = response['data'] || [];

        if (newData.length === 0 || newData.length < this.limit) {
          this.allTableDataLoaded = true;
        }

        // Only process the newly received data
        const pendingPaxOrderKeys: string[] = [];

        // Check for pending orders
        newData.forEach((order: any) => {
          const parcelDetailsExist = order.parcelDetails && order.parcelDetails.length > 0;
          if (!parcelDetailsExist) {
            pendingPaxOrderKeys.push(order.paxOrderKey);
          }
        });

        // 🆕 Fetch all pending statuses in bulk
        if (pendingPaxOrderKeys.length > 0) {
          this.fetchValidationErrorsForPendingOrders(pendingPaxOrderKeys);
        }

        // Replace list on fresh search (offset 0), append on scroll
        if (this.offset === 0) {
          this.ordersList.set(newData);
        } else {
          this.ordersList.update(current => current.concat(newData));
        }

        // Expand rows with parcels not in searchedStatuses
        this.expandedRows = {};
        this.adjustedFilteredOrdersList().forEach((order, i) => {
          if (
            this.selectedStatuses().length > 0 && // Only expand if a status is selected
            order.parcelDetails &&
            order.parcelDetails.some(parcel => !this.selectedStatuses().includes(parcel.operationalParcelStatus))
          ) {
            this.expandedRows[i] = true;
          }
        });

        this.limit = response['limit'];
        this.totalFoundItems = response['total'];
        this.offset += this.limit;
        setTimeout(() => {
          this.checkScrollAfterDataUpdate();
        }, 100); // Let DOM update first
        this.isTableLoadingMore = false;
        this.dataNotLoaded = false;
      },
      error: (error) => {
        if (currentRequestId !== this.lastRequestId) return; // Ignore outdated response if a newer request was initiated

        this.isTableLoadingMore = false;
        this.dataNotLoaded = true;
        this.ordersList.set([]);
        this.totalFoundItems = 0;

        setTimeout(() => {
          if (error.status === 401) {
            this.authorizationFailed = true;
          }
        }, 2000);
      }
    });
  }

  fetchValidationErrorsForPendingOrders(paxOrderKeys: string[]): void {
    this.paxOrderService.getMultipleShipItStatuses(paxOrderKeys)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: (result) => {
        if (Array.isArray(result)) {
          for (const item of result) {
            this.paxOrderValdationsErrorList[item.paxOrderKey] = item;
          }
        } else {
          console.warn('Unexpected response format:', result);
        }
      },
      error: (error) => {
        console.error('Failed to fetch bulk statuses:', error);
      }
    });
  }

  onTableScroll(event: any): void {
    const element = event.target;
    const currentScrollTop = element.scrollTop;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10;
    const isScrollingDown = currentScrollTop > this.lastScrollTop;

    // Track scroll direction
    if (isScrollingDown) {
      this.lastScrollDirection = 'down';
    } else {
      this.lastScrollDirection = 'up';
      this.popupCooldown = false; // Reset popup availability when user scrolls up
    }

    if (atBottom) {
      if (!this.isTableLoadingMore && !this.allTableDataLoaded) {
        this.updateTable();
      } else if (this.allTableDataLoaded && this.lastScrollDirection === 'down') {
        if (!this.popupCooldown) {
          this.triggerEndOfListPopup();
          this.popupCooldown = true;
        }
      }
    }

    this.lastScrollTop = currentScrollTop;
  }

  triggerEndOfListPopup(): void {
    if (this.isPopupShowing) return; // prevent retrigger

    this.isPopupShowing = true;
    this.alertPopup.showMessage('You reached the End of the table!');

    setTimeout(() => {
      this.alertPopup.closePopup();
      this.isPopupShowing = false; // allow future popups again
    }, 1500);
  }

  checkScrollAfterDataUpdate() {
    const element = document.querySelector('.table-scroll-container') as HTMLElement;
    if (!element) return;

    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10;
    if (atBottom && !this.isTableLoadingMore && !this.allTableDataLoaded) {
      this.updateTable();
    }
  }

  pageChanged() {
    this.offset = (this.currentPage - 1) * this.limit;
    this.queryParameters = {};
    this.queryParameters['limit'] = this.limit;
    this.queryParameters['offset'] = this.offset;
    this.limit = this.queryParameters['limit'];
    this.expandedRows = {};
    this.setQueryParameters();
    this.saveCurrentOffsetToLocalStorage();
    this.paxOrderService.getOrders(this.queryParameters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: (response) => {
        this.ordersList.set(response['data']);
        this.limit = response['limit'];
        this.totalFoundItems = response['total'];
        this.totalPages = Math.ceil(this.totalFoundItems / this.limit);
        this.dataNotLoaded = false;
      },
      error: () => {
        this.dataNotLoaded = true;
      }
    });
  }

  resetOffsetAndOrderList() {
    this.offset = 0;
    this.allTableDataLoaded = false;
    this.ordersList.set([]);
  }

  loadCurrentOffsetFromLocalStorage() {
    const savedOffset = localStorage.getItem('offsetForOrders');
    this.offset = savedOffset ? parseInt(savedOffset, 10) : 0;
  }

  saveCurrentOffsetToLocalStorage() {
    localStorage.setItem('offsetForOrders', String(this.offset));
  }



  /** Function to get only date from the response insertTimestamp */
  dateOfInsertTimestamp(insertTimestamp: any) {
    return insertTimestamp ? insertTimestamp.substring(0, 10) : '';
  }

  /** Function to trigger searching with a specific timeout. */
  onInputChange(event: any) {
    this.resetOffsetAndOrderList();
    clearTimeout(this.timerForTyping);
    this.timerForTyping = setTimeout(() => {
      this.updateTable();
    }, 600);
  }

  /** Function to get pickup date input from user */
  onChangePickUpDate() {
    this.resetOffsetAndOrderList();
    this.requestedPickupDateGt.set('');
    this.requestedPickupDateLte.set('');

    if (this.selectedRequestedPickupDateGt()) {
      // Ensure if having a valid date object
      const startDate = this.parseAndValidateDate(this.selectedRequestedPickupDateGt());
      if (startDate) {
        this.requestedPickupDateGt.set(this.getPreviousDate(startDate));
      }
    }

    if (this.selectedRequestedPickupDateLte()) {
      // Ensure if having a valid date object
      const endDate = this.parseAndValidateDate(this.selectedRequestedPickupDateLte());
      if (endDate) {
        this.requestedPickupDateLte.set(this.datePipe.transform(endDate, 'yyyy-MM-dd') || '');
      }
    }

    this.saveFiltersToLocalStorage();

    this.timerForTyping = setTimeout(() => {
      this.updateTable();
    }, 600);
  }

  /** Function to get creation date input from user */
  onChangeCreationDate() {
    this.resetOffsetAndOrderList();
    this.requestedCreationDateGt.set('');
    this.requestedCreationDateLte.set('');

    if (this.selectedRequestedCreationDateGt()) {
      // Ensure if having a valid date object
      const startDate = this.parseAndValidateDate(this.selectedRequestedCreationDateGt());
      if (startDate) {
        const dateGt = this.getPreviousDate(startDate) + "T23:59:59Z";
        this.requestedCreationDateGt.set(dateGt);
        localStorage.removeItem('creationStartDateRemoved');
      }
    }

    if (this.selectedRequestedCreationDateLte()) {
      // Ensure we have a valid date object
      const endDate = this.parseAndValidateDate(this.selectedRequestedCreationDateLte());
      if (endDate) {
        this.requestedCreationDateLte.set(this.datePipe.transform(endDate, 'yyyy-MM-dd') + "T23:59:59Z");
        localStorage.removeItem('creationEndDateRemoved');
      }
    }

    this.saveFiltersToLocalStorage();

    this.timerForTyping = setTimeout(() => {
      this.updateTable();
    }, 600);
  }

  /**
   * Handle manual date input changes (when user types dates directly)
   * Only clear empty inputs immediately, delay validation for partial inputs
   */
  onCreationDateInputChange(event: any, isStartDate: boolean) {
    const inputValue = event.target?.value || event;

    // Only handle empty inputs immediately to clear the date
    if (!inputValue || inputValue.trim() === '') {
      if (isStartDate) {
        this.selectedRequestedCreationDateGt.set('');
      } else {
        this.selectedRequestedCreationDateLte.set('');
      }
      this.onChangeCreationDate();
      return;
    }
  }

  /**
   * Handle creation date blur event - validate and format on focus loss
   */
  onCreationDateBlur(event: any, isStartDate: boolean) {
    // Prevent multiple blur event processing
    if (this.processingDateBlur) {
      return;
    }

    this.processingDateBlur = true;
    const inputValue = event.target?.value;

    if (!inputValue) {
      // Clear the date if input is empty
      if (isStartDate) {
        this.selectedRequestedCreationDateGt.set('');
        localStorage.setItem('creationStartDateRemoved', 'true');
      } else {
        this.selectedRequestedCreationDateLte.set('');
        localStorage.setItem('creationEndDateRemoved', 'true');
      }
      this.processingDateBlur = false;
      this.onChangeCreationDate();
      return;
    }

    const parsedDate = this.parseAndValidateDate(inputValue);
    if (parsedDate) {
      // Update the model with the parsed date
      if (isStartDate) {
        this.selectedRequestedCreationDateGt.set(parsedDate);
        localStorage.removeItem('creationStartDateRemoved');
      } else {
        this.selectedRequestedCreationDateLte.set(parsedDate);
        localStorage.removeItem('creationEndDateRemoved');
      }

      // Use multiple timeouts to forcefully override Angular Material's formatting
      const formattedDate = this.datePipe.transform(parsedDate, 'dd/MM/yyyy');
      if (formattedDate && event.target) {
        event.target.value = formattedDate;

        setTimeout(() => {
          if (event.target && event.target.value !== formattedDate) {
            event.target.value = formattedDate;
          }
        }, 0);

        setTimeout(() => {
          if (event.target && event.target.value !== formattedDate) {
            event.target.value = formattedDate;
          }
        }, 10);
      }

      setTimeout(() => {
        this.processingDateBlur = false;
      }, 50);

      this.onChangeCreationDate();
    } else {
      // Invalid date - revert to previous valid value or clear
      console.warn('Invalid date format. Please use DD/MM/YYYY format.');

      const currentDate = isStartDate ? this.selectedRequestedCreationDateGt() : this.selectedRequestedCreationDateLte();

      if (currentDate && currentDate instanceof Date) {
        // Revert to the formatted version of the current valid date
        setTimeout(() => {
          const formattedDate = this.datePipe.transform(currentDate, 'dd/MM/yyyy');
          if (formattedDate && event.target) {
            event.target.value = formattedDate;
          }
        }, 0);
      } else {
        // No valid current date, clear the field
        event.target.value = '';
        if (isStartDate) {
          this.selectedRequestedCreationDateGt.set('');
          localStorage.setItem('creationStartDateRemoved', 'true');
        } else {
          this.selectedRequestedCreationDateLte.set('');
          localStorage.setItem('creationEndDateRemoved', 'true');
        }
        this.onChangeCreationDate();
      }

      setTimeout(() => {
        this.processingDateBlur = false;
      }, 50);
    }
  }

  /**
   * Handle keydown events for creation date inputs to provide better UX
   */
  onCreationDateKeydown(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End'
    ];

    if (event.key === 'Tab') {
      const target = event.target as HTMLInputElement;
      const isStartDate = target?.id === 'searchRequestedCreationDateGt';

      const currentValue = target?.value;

      event.preventDefault();

      if (currentValue) {
        const parsedDate = this.parseAndValidateDate(currentValue);
        if (parsedDate) {
          const formattedDate = this.datePipe.transform(parsedDate, 'dd/MM/yyyy');
          if (formattedDate && target) {
            target.value = formattedDate;
          }

          if (isStartDate) {
            this.selectedRequestedCreationDateGt.set(parsedDate);
            localStorage.removeItem('creationStartDateRemoved');
          } else {
            this.selectedRequestedCreationDateLte.set(parsedDate);
            localStorage.removeItem('creationEndDateRemoved');
          }

          this.onChangeCreationDate();
        }
      }

      setTimeout(() => {
        const focusableElements = Array.from(document.querySelectorAll(
          'input:not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )) as HTMLElement[];

        const currentIndex = focusableElements.indexOf(target);
        if (currentIndex !== -1 && currentIndex < focusableElements.length - 1) {
          focusableElements[currentIndex + 1].focus();
        }
      }, 0);

      return;
    }

    const isNumber = (event.key >= '0' && event.key <= '9');
    const isDot = event.key === '.';
    const isSlash = event.key === '/';

    if (!allowedKeys.includes(event.key) && !isNumber && !isDot && !isSlash) {
      event.preventDefault();
    }
  }

  /**
   * Handle Enter key press on creation date inputs - validate and apply the date
   */
  onCreationDateEnterPressed(event: any, isStartDate: boolean) {
    // Trigger blur behavior when Enter is pressed to validate the input
    this.onCreationDateBlur(event, isStartDate);
    // Remove focus from the input to hide the datepicker
    event.target.blur();
  }

  /**
   * Handle manual pickup date input changes (when user types dates directly)
   * Only clear empty inputs immediately, delay validation for partial inputs
   */
  onPickupDateInputChange(event: any, isStartDate: boolean) {
    const inputValue = event.target?.value || event;

    // Only handle empty inputs immediately to clear the date
    if (!inputValue || inputValue.trim() === '') {
      if (isStartDate) {
        this.selectedRequestedPickupDateGt.set('');
      } else {
        this.selectedRequestedPickupDateLte.set('');
      }
      this.onChangePickUpDate();
      return;
    }
  }

  /**
   * Handle pickup date blur event - validate and format on focus loss
   */
  onPickupDateBlur(event: any, isStartDate: boolean) {
    // Prevent multiple blur event processing
    if (this.processingDateBlur) {
      return;
    }

    this.processingDateBlur = true;
    const inputValue = event.target?.value;

    if (!inputValue) {
      // Clear the date if input is empty
      if (isStartDate) {
        this.selectedRequestedPickupDateGt.set('');
      } else {
        this.selectedRequestedPickupDateLte.set('');
      }
      this.processingDateBlur = false;
      this.onChangePickUpDate();
      return;
    }

    const parsedDate = this.parseAndValidateDate(inputValue);
    if (parsedDate) {
      // Update the model with the parsed date
      if (isStartDate) {
        this.selectedRequestedPickupDateGt.set(parsedDate);
      } else {
        this.selectedRequestedPickupDateLte.set(parsedDate);
      }

      // Use multiple timeouts to forcefully override Angular Material's formatting
      const formattedDate = this.datePipe.transform(parsedDate, 'dd/MM/yyyy');
      if (formattedDate && event.target) {
        event.target.value = formattedDate;

        setTimeout(() => {
          if (event.target && event.target.value !== formattedDate) {
            event.target.value = formattedDate;
          }
        }, 0);

        setTimeout(() => {
          if (event.target && event.target.value !== formattedDate) {
            event.target.value = formattedDate;
          }
        }, 10);
      }

      setTimeout(() => {
        this.processingDateBlur = false;
      }, 50);

      this.onChangePickUpDate();
    } else {
      // Invalid date - revert to previous valid value or clear
      console.warn('Invalid date format. Please use DD/MM/YYYY format.');

      const currentDate = isStartDate ? this.selectedRequestedPickupDateGt() : this.selectedRequestedPickupDateLte();

      if (currentDate && currentDate instanceof Date) {
        // Revert to the formatted version of the current valid date
        setTimeout(() => {
          const formattedDate = this.datePipe.transform(currentDate, 'dd/MM/yyyy');
          if (formattedDate && event.target) {
            event.target.value = formattedDate;
          }
        }, 0);
      } else {
        // No valid current date, clear the field
        event.target.value = '';
        if (isStartDate) {
          this.selectedRequestedPickupDateGt.set('');
        } else {
          this.selectedRequestedPickupDateLte.set('');
        }
        this.onChangePickUpDate();
      }

      setTimeout(() => {
        this.processingDateBlur = false;
      }, 50);
    }
  }

  /**
   * Handle keydown events for pickup date inputs to provide better UX
   */
  onPickupDateKeydown(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End'
    ];

    if (event.key === 'Tab') {
      const target = event.target as HTMLInputElement;
      const isStartDate = target?.id === 'searchRequestedPickupDateGt';

      const currentValue = target?.value;

      event.preventDefault();

      if (currentValue) {
        const parsedDate = this.parseAndValidateDate(currentValue);
        if (parsedDate) {
          const formattedDate = this.datePipe.transform(parsedDate, 'dd/MM/yyyy');
          if (formattedDate && target) {
            target.value = formattedDate;
          }

          if (isStartDate) {
            this.selectedRequestedPickupDateGt.set(parsedDate);
          } else {
            this.selectedRequestedPickupDateLte.set(parsedDate);
          }

          this.onChangePickUpDate();
        }
      }

      setTimeout(() => {
        const focusableElements = Array.from(document.querySelectorAll(
          'input:not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )) as HTMLElement[];

        const currentIndex = focusableElements.indexOf(target);
        if (currentIndex !== -1 && currentIndex < focusableElements.length - 1) {
          focusableElements[currentIndex + 1].focus();
        }
      }, 0);

      return;
    }

    const isNumber = (event.key >= '0' && event.key <= '9');
    const isDot = event.key === '.';
    const isSlash = event.key === '/';

    if (!allowedKeys.includes(event.key) && !isNumber && !isDot && !isSlash) {
      event.preventDefault();
    }
  }

  /**
   * Handle Enter key press on pickup date inputs - validate and apply the date
   */
  onPickupDateEnterPressed(event: any, isStartDate: boolean) {
    // Trigger blur behavior when Enter is pressed to validate the input
    this.onPickupDateBlur(event, isStartDate);
    // Remove focus from the input to hide the datepicker
    event.target.blur();
  }

  /** Function to return the previous date */
  getPreviousDate(date: Date | string): string {
    let mainDate: Date;

    // Handle both Date objects and string inputs
    if (date instanceof Date) {
      mainDate = new Date(date);
    } else {
      mainDate = this.parseAndValidateDate(date);
    }

    if (!mainDate || isNaN(mainDate.getTime())) {
      console.error('Invalid date provided to getPreviousDate:', date);
      return '';
    }

    // Create previous day without modifying original date
    let previousDay = new Date(mainDate.getTime());
    previousDay.setDate(previousDay.getDate() - 1);

    // Format as YYYY-MM-DD
    return this.datePipe.transform(previousDay, 'yyyy-MM-dd') || '';
  }

  /**
   * Function to parse and validate date input handling various formats
   * Supports: Date objects, ISO strings, DD.MM.YYYY, DD/MM/YYYY, YYYY-MM-DD
   * Prevents day/month swapping by enforcing German locale expectations
   */
  parseAndValidateDate(dateInput: Date | string): Date | null {
    if (!dateInput) {
      return null;
    }

    // If already a Date object, validate and return
    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? null : dateInput;
    }

    // Handle string inputs
    let dateString = dateInput.toString().trim();

    if (!dateString) {
      return null;
    }

    let parsedDate: Date;

    try {
      // Try different date formats with proper validation
      if (dateString.includes('.')) {
        // Handle DD.MM.YYYY format (German format) - PRIORITY FORMAT
        const parts = dateString.split('.');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
          const year = parseInt(parts[2], 10);

          // Validate ranges more strictly
          if (day >= 1 && day <= 31 && month >= 0 && month <= 11 && year >= 1900 && year <= 2100) {
            parsedDate = new Date(year, month, day, 12, 0, 0, 0); // Set to noon to avoid timezone issues

            // Additional validation: ensure the date actually exists (handles Feb 30, etc.)
            if (parsedDate.getDate() === day && parsedDate.getMonth() === month && parsedDate.getFullYear() === year) {
              return parsedDate;
            } else {
              throw new Error('Invalid date - date does not exist');
            }
          } else {
            throw new Error('Date parts out of range');
          }
        } else {
          throw new Error('Invalid DD.MM.YYYY format');
        }
      } else if (dateString.includes('/')) {
        // Handle DD/MM/YYYY format - treating as DD/MM/YYYY (day first, European style)
        const parts = dateString.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
          const year = parseInt(parts[2], 10);

          // Validate ranges
          if (day >= 1 && day <= 31 && month >= 0 && month <= 11 && year >= 1900 && year <= 2100) {
            parsedDate = new Date(year, month, day, 12, 0, 0, 0);

            // Validate the date actually exists
            if (parsedDate.getDate() === day && parsedDate.getMonth() === month && parsedDate.getFullYear() === year) {
              return parsedDate;
            } else {
              throw new Error('Invalid date - date does not exist');
            }
          } else {
            throw new Error('Date parts out of range');
          }
        } else {
          throw new Error('Invalid DD/MM/YYYY format');
        }
      } else if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Handle YYYY-MM-DD format (ISO format)
        const parts = dateString.split('-');
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const day = parseInt(parts[2], 10);

        if (day >= 1 && day <= 31 && month >= 0 && month <= 11 && year >= 1900 && year <= 2100) {
          parsedDate = new Date(year, month, day, 12, 0, 0, 0);

          if (parsedDate.getDate() === day && parsedDate.getMonth() === month && parsedDate.getFullYear() === year) {
            return parsedDate;
          } else {
            throw new Error('Invalid date - date does not exist');
          }
        } else {
          throw new Error('Date parts out of range');
        }
      } else {
        // Try standard Date parsing as last resort (handles ISO strings, etc.)
        parsedDate = new Date(dateString);

        // For standard parsing, be extra careful about invalid dates
        if (isNaN(parsedDate.getTime())) {
          throw new Error('Standard parsing failed');
        }

        // Set to noon to avoid timezone issues
        parsedDate.setHours(12, 0, 0, 0);
        return parsedDate;
      }

    } catch (error) {
      console.warn('Date parsing error for input:', dateInput, 'Error:', error.message);
      return null;
    }

    return null;
  }

  /**
   * Helper method to format date for display consistently
   */
  formatDateForDisplay(date: Date | string): string | null {
    if (!date) {
      return null;
    }

    try {
      // If it's already a Date object, format it
      if (date instanceof Date) {
        return this.datePipe.transform(date, 'dd/MM/yyyy');
      }

      // If it's a string, try to parse and format
      const parsedDate = this.parseAndValidateDate(date);
      if (parsedDate) {
        return this.datePipe.transform(parsedDate, 'dd/MM/yyyy');
      }

      return null;
    } catch (error) {
      console.error('Error in formatDateForDisplay:', error, 'Input:', date);
      return null;
    }
  }

  /** Function to go to createPaxOrder Page and use orders as a template */
  useExistingOrderAsTemplate(item: any) {
    let selectedOrder = this.ordersList().filter((order: any) => order['paxOrderKey'] === item.paxOrderKey);
    localStorage.setItem("paxOrderToUseAsTemplate", JSON.stringify(selectedOrder[0]));
    localStorage.setItem("fromTemplateAction", "true");
    this.router.navigate(['/create-order']);
  }

  resetPickupDatePicker() {
    this.resetOffsetAndOrderList();
    this.requestedPickupDateGt.set('');
    this.requestedPickupDateLte.set('');
    this.selectedRequestedPickupDateGt.set('');
    this.selectedRequestedPickupDateLte.set('');
    this.updateTable();
    this.saveFiltersToLocalStorage();
  }

  resetCreationDatePicker() {
    this.resetOffsetAndOrderList();
    this.requestedCreationDateGt.set('');
    this.requestedCreationDateLte.set('');
    this.selectedRequestedCreationDateGt.set('');
    this.selectedRequestedCreationDateLte.set('');
    localStorage.setItem('creationStartDateRemoved', 'true');
    localStorage.setItem('creationEndDateRemoved', 'true');
    this.updateTable();
    this.saveFiltersToLocalStorage();
  }

  /** Pick-up Date functionality :
   without parameter sets standardDate to today,
   with parameter adds the toAddDays to current date.
   The result date will be yyyy-mm-dd */
  getStandardDate(toAddDays: number = 0): string {
    let date = new Date();

    // Set time to noon to avoid timezone issues
    date.setHours(12, 0, 0, 0);
    date.setDate(date.getDate() + toAddDays);

    // Adjust for weekends only if the resulting date falls on the weekend
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 6) {
      // Saturday, move to Monday
      date.setDate(date.getDate() + 2);
    } else if (dayOfWeek === 0) {
      // Sunday, move to Monday
      date.setDate(date.getDate() + 1);
    }

    // Use DatePipe for consistent formatting
    return this.datePipe.transform(date, 'yyyy-MM-dd') || '';
  }

  onNewOrderClick() {
    localStorage.removeItem("seePaxOrderDetails");
    localStorage.removeItem("paxOrderToUseAsTemplate");
    localStorage.removeItem("paxOrderForEdit");
  }

  onSeeOrderDetailsClick(item: any) {
    let selectedOrder = this.ordersList().filter((order: any) => order['paxOrderKey'] === item.paxOrderKey);
    localStorage.setItem("seePaxOrderDetails", JSON.stringify(selectedOrder[0]));
    this.saveCurrentOffsetToLocalStorage();
    this.router.navigate(['/order-details']);
  }

  stopEvent(event: Event) {
    event.stopPropagation();
  }

  onOrderStatusSelect(selectedStatuses: string[]) {
    this.resetOffsetAndOrderList();
    this.selectedStatuses.set(selectedStatuses);
    this.orderStatus.set(this.selectedStatuses().join(','));
    this.updateTable();
  }

  onPendingParcelClick(item: any) {
    if (item.statusKey !== 'PARCELS_CREATED') {
        this.showAlert(this.formatShipItErrors(item));
    }
  }

  getShipItValidationErrors(paxOrderKey: string) {
    this.paxOrderService.getShipItResponse(paxOrderKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: (v) => this.paxOrderValdationsErrorList[paxOrderKey]= v,
      error: (e) => this.paxOrderValdationsErrorList[paxOrderKey]= e
    })
  }


  onEditOrderClick(item: any) {
    let selectedOrder = this.ordersList().filter((order: any) => order['paxOrderKey'] === item.paxOrderKey);
    localStorage.setItem("paxOrderForEdit", JSON.stringify(selectedOrder[0]));
    localStorage.setItem("paxOrderValdationsErrorList",  JSON.stringify(this.paxOrderValdationsErrorList[item.paxOrderKey]));
    this.saveCurrentOffsetToLocalStorage();
    this.router.navigate(['/edit-order']);
  }

  formatShipItErrors(item: any): string {
    let errors = this.paxOrderValdationsErrorList[item.paxOrderKey];

    if (!errors.hasOwnProperty('validationErrors')) {
      return "Not allowed to see the validation errors for this order.";
    }

    if (errors['validationErrors'].length < 1) {
      return "Order is still being processed, check again later."
    }

    var formattedErrors = "The following values contain validation errors and need to be resolved: \n\n";

    return formattedErrors += errors['validationErrors'].map((error: any) =>
      `${this.replaceTechnicalWithUINames(error.fieldName)}: ${error.fieldValue}`
    ).join('\n\n');
  }

  replaceTechnicalWithUINames(fieldName: string): string {
    return fieldName.replace("sender", "requester").replace("recipient", "pickup");
  }

  onOptionsClick(event: MouseEvent, index: number) {
    event.stopPropagation();
    this.popoverIndex = this.popoverIndex === index ? null : index;
    if (this.popoverIndex !== null) {
      document.addEventListener('click', this.documentClickListener);
    } else {
      document.removeEventListener('click', this.documentClickListener);
    }
  }

  documentClickListener = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.popup') && !target.closest('.three-points-settings-icon')) {
      this.popoverIndex = null;
      document.removeEventListener('click', this.documentClickListener);
    }
  };

  @ViewChild('alertPopup') alertPopup: PopupMessageComponent;

  showAlert(message: any): void {
    this.alertPopup.showMessage(message);
  }

  /**Resizing logic */
  startResize(event: MouseEvent, th: HTMLElement): void {
    this.isResizing = true;
    this.startingX = event.pageX;
    this.startingWidth = th.offsetWidth;

    this.resizingMouseMove = this.performResize.bind(this, th);
    this.resizingMouseUp = this.stopResize.bind(this);

    document.addEventListener('mousemove', this.resizingMouseMove);
    document.addEventListener('mouseup', this.resizingMouseUp);
  }

  performResize(th: HTMLElement, event: MouseEvent): void {
    if (this.isResizing) {
      const movementX = event.pageX - this.startingX;
      th.style.width = `${this.startingWidth + movementX}px`;
    }
  }

  stopResize(): void {
    this.isResizing = false;

    if (this.resizingMouseMove) {
      document.removeEventListener('mousemove', this.resizingMouseMove);
    }
    if (this.resizingMouseUp) {
      document.removeEventListener('mouseup', this.resizingMouseUp);
    }
  }

  onParcelNumberSortingClick() {
    this.resetOffsetAndOrderList();
    const ascendingKey = "parcelnumber";
    const descendingKey = "-parcelnumber";
    const existingIndex = this.sortingList().findIndex(item => item === ascendingKey || item === descendingKey);

    if (this.isParcelNumberAscendingSortingActive()) {
        this.sortingList.update(list => {
          const newList = [...list];
          newList.splice(existingIndex, 1);
          return newList;
        });
    } else if (this.isParcelNumberDescendingSortingActive()) {
        this.sortingList.update(list => {
          const newList = [...list];
          newList[existingIndex] = ascendingKey;
          return newList;
        });
    } else {
        this.sortingList.update(list => {
          const newList = [...list];
          if (existingIndex > -1) {
            newList[existingIndex] = descendingKey;
          } else {
            newList.push(descendingKey);
          }
          return newList;
        });
    }

    this.updateTable();
    this.saveSortingChipsToLocalStorage();
  }

  onCreationDateSortingClick() {
    this.resetOffsetAndOrderList();
    const ascendingKey = "inserttimestamp";
    const descendingKey = "-inserttimestamp";
    const existingIndex = this.sortingList().findIndex(item => item === ascendingKey || item === descendingKey);

    if (this.isCreationDateAscendingSortingActive()) {
        this.sortingList.update(list => {
          const newList = [...list];
          newList.splice(existingIndex, 1);
          return newList;
        });
    } else if (this.isCreationDateDescendingSortingActive()) {
        this.sortingList.update(list => {
          const newList = [...list];
          newList[existingIndex] = ascendingKey;
          return newList;
        });
    } else {
        this.sortingList.update(list => {
          const newList = [...list];
          if (existingIndex > -1) {
            newList[existingIndex] = descendingKey;
          } else {
            newList.push(descendingKey);
          }
          return newList;
        });
    }

    this.updateTable();
    this.saveSortingChipsToLocalStorage();
  }

  onRequesterLocationSortingClick() {
    this.resetOffsetAndOrderList();
    const ascendingKey = "requesterlocation";
    const descendingKey = "-requesterlocation";
    const existingIndex = this.sortingList().findIndex(item => item === ascendingKey || item === descendingKey);

    if (this.isRequesterLocationAscendingSortingActive()) {
        this.sortingList.update(list => {
          const newList = [...list];
          newList.splice(existingIndex, 1);
          return newList;
        });
    } else if (this.isRequesterLocationDescendingSortingActive()) {
        this.sortingList.update(list => {
          const newList = [...list];
          newList[existingIndex] = ascendingKey;
          return newList;
        });
    } else {
        this.sortingList.update(list => {
          const newList = [...list];
          if (existingIndex > -1) {
            newList[existingIndex] = descendingKey;
          } else {
            newList.push(descendingKey);
          }
          return newList;
        });
    }

    this.updateTable();
    this.saveSortingChipsToLocalStorage();
  }

  onPickupLocationSortingClick() {
    this.resetOffsetAndOrderList();
    const ascendingKey = "pickuplocation";
    const descendingKey = "-pickuplocation";
    const existingIndex = this.sortingList().findIndex(item => item === ascendingKey || item === descendingKey);

    if (this.isPickupLocationAscendingSortingActive()) {
        this.sortingList.update(list => {
          const newList = [...list];
          newList.splice(existingIndex, 1);
          return newList;
        });
    } else if (this.isPickupLocationDescendingSortingActive()) {
        this.sortingList.update(list => {
          const newList = [...list];
          newList[existingIndex] = ascendingKey;
          return newList;
        });
    } else {
        this.sortingList.update(list => {
          const newList = [...list];
          if (existingIndex > -1) {
            newList[existingIndex] = descendingKey;
          } else {
            newList.push(descendingKey);
          }
          return newList;
        });
    }

    this.updateTable();
    this.saveSortingChipsToLocalStorage();
  }


  onDeliveryLocationSortingClick() {
    this.resetOffsetAndOrderList();
    const ascendingKey = "deliverylocation";
    const descendingKey = "-deliverylocation";
    const existingIndex = this.sortingList().findIndex(item => item === ascendingKey || item === descendingKey);

    if (this.isDeliveryLocationAscendingSortingActive()) {
        this.sortingList.update(list => {
          const newList = [...list];
          newList.splice(existingIndex, 1);
          return newList;
        });
    } else if (this.isDeliveryLocationDescendingSortingActive()) {
        this.sortingList.update(list => {
          const newList = [...list];
          newList[existingIndex] = ascendingKey;
          return newList;
        });
    } else {
        this.sortingList.update(list => {
          const newList = [...list];
          if (existingIndex > -1) {
            newList[existingIndex] = descendingKey;
          } else {
            newList.push(descendingKey);
          }
          return newList;
        });
    }

    this.updateTable();
    this.saveSortingChipsToLocalStorage();
  }



  getDisplayKeyForSort(key: string): string {
    const displayKeys: { [key: string]: string } = {
      parcelnumber: 'Parcel Number',
      inserttimestamp: 'Creation Date',
      requesterlocation: 'Requester Location',
      pickuplocation: 'Pick-up Location',
      deliverylocation: 'Delivery Location'
    };

    return displayKeys[key] || key;
  }

  removeSortingChips(sort: { key: string, order: 'asc' | 'desc' }) {
    this.resetOffsetAndOrderList();
    if (sort.key === 'Remove All Sortings') {
      this.removeAllSorting();
      this.updateTable();
      return;
    }

    const keyMapping: { [key: string]: string } = {
      'Parcel Number': 'parcelnumber',
      'Creation Date': 'inserttimestamp',
      'Requester Location': 'requesterlocation',
      'Pick-up Location': 'pickuplocation',
      'Delivery Location': 'deliverylocation',
    };

    const actualKey = keyMapping[sort.key] || sort.key;
    const formattedKey = sort.order === 'asc' ? actualKey : `-${actualKey}`;

    const index = this.sortingList().indexOf(formattedKey);
    if (index > -1) {
      this.sortingList.update(list => {
        const newList = [...list];
        newList.splice(index, 1);
        return newList;
      });
    }

    this.updateTable();
    this.saveSortingChipsToLocalStorage();
  }

  removeAllSorting() {
    this.resetOffsetAndOrderList();
    this.sortingList.set([]);
    this.saveSortingChipsToLocalStorage();
  }

  onRemoveAllSortingsClick() {
    this.removeAllSorting();
    this.updateTable();
  }

  saveSortingChipsToLocalStorage() {
    const sortingState = {
      sortingList: this.sortingList()
    };

    localStorage.setItem('sortingChipsStateForMyOrders', JSON.stringify(sortingState));
  }

  loadSortingChipsFromLocalStorage() {
    const storedSortingState = localStorage.getItem('sortingChipsStateForMyOrders');

    if (storedSortingState) {
      const sortingState = JSON.parse(storedSortingState);
      this.sortingList.set(sortingState.sortingList || []);
    }
  }

  onResetToDefaultFiltersClick() {
    this.removeAllFilters();
    this.removeAllSorting();

    this.selectedRequestedCreationDateGt.set(this.getStandardDate(-6));
    const dateGt = this.getPreviousDate(this.selectedRequestedCreationDateGt());
    this.requestedCreationDateGt.set(dateGt);
    this.selectedRequestedCreationDateLte.set(this.getStandardDate(0));

    const requestedGt = this.requestedCreationDateGt();
    this.requestedCreationDateGt.set(
      requestedGt && requestedGt.includes('T')
        ? requestedGt
        : requestedGt + "T23:59:59Z"
    );

    const selectedLte = this.selectedRequestedCreationDateLte();
    this.requestedCreationDateLte.set(
      selectedLte && typeof selectedLte === 'string' && selectedLte.includes('T')
        ? selectedLte
        : selectedLte + "T23:59:59Z"
    );

    localStorage.removeItem('creationStartDateRemoved');
    localStorage.removeItem('creationEndDateRemoved');

    this.sortingList.set(["-inserttimestamp"]);

    this.setQueryParameters();
    this.saveFiltersToLocalStorage();
    this.saveSortingChipsToLocalStorage();
    this.saveCurrentOffsetToLocalStorage();
    this.updateTable();
  }

  sortParcelDetailsAscending(parcelDetails: any[]): any[] {
    if (!parcelDetails) return [];
    return [...parcelDetails].sort((a, b) => a.parcelKey.localeCompare(b.parcelKey));
  }

  sortParcelDetailsBySelectedStatus(parcelDetails: any[]): any[] {
    if (!this.selectedStatuses() || this.selectedStatuses().length === 0) {
      return this.sortParcelDetailsAscending(parcelDetails);
    }
    return parcelDetails.slice().sort((a, b) => {
      const aSelected = this.selectedStatuses().includes(a.operationalParcelStatus) ? 0 : 1;
      const bSelected = this.selectedStatuses().includes(b.operationalParcelStatus) ? 0 : 1;
      return aSelected - bSelected;
    });
  }
}
